﻿import { z } from 'zod';

/** A site is the customer-network gateway that carries the management plane to its CPEs. */
export const tunnelTypeSchema = z.enum(['wg', 'ovpn']);
export type TunnelType = z.infer<typeof tunnelTypeSchema>;

export const routerVendorSchema = z.enum(['mikrotik', 'other']);
export type RouterVendor = z.infer<typeof routerVendorSchema>;

export const siteStatusSchema = z.enum(['up', 'down', 'pending']);
export type SiteStatus = z.infer<typeof siteStatusSchema>;

/**
 * Whether the gateway translates its tenant's ranges onto platform blocks at
 * the tunnel (2026-09-17).
 *
 *   none    the ranges themselves are routed on the tunnel — every gateway
 *           made before this existed, and a router that cannot NAT.
 *   netmap  the platform issues a block of its own per range (NETMAP_POOL)
 *           and the router maps the two one-to-one, so the tenant's
 *           addressing stays its own and may overlap another tenant's.
 */
export const addressTranslationSchema = z.enum(['none', 'netmap']);
export type AddressTranslation = z.infer<typeof addressTranslationSchema>;

/**
 * WHERE a translated gateway's ranges are mapped onto the platform blocks
 * (2026-09-19):
 *   router  the MikroTik's own netmap NAT rules, written by the router script;
 *   server  the ACS host, on the gateway's own WireGuard interface — the
 *           router script then carries no NAT rule, only the tunnel and the
 *           forward accepts. The operator's preference, and the default for
 *           a new gateway.
 */
export const translationAtSchema = z.enum(['router', 'server']);
export type TranslationAt = z.infer<typeof translationAtSchema>;

export const cidrSchema = z
  .string()
  .regex(
    /^(\d{1,3}\.){3}\d{1,3}\/([0-9]|[12][0-9]|3[0-2])$/,
    'expected an IPv4 CIDR such as 10.10.0.0/16',
  );

/**
 * A network routed through a tunnel, with an optional label saying what it is
 * for. A gateway typically carries several (ONU management, OLT management, a
 * customer range) and a bare list of CIDRs does not say which is which.
 *
 * Accepts a plain string so rows written before labels existed still parse.
 */
export const managedSubnetSchema = z.preprocess(
  (value) => (typeof value === 'string' ? { cidr: value } : value),
  z.object({
    cidr: cidrSchema,
    label: z.string().max(60).nullable().optional(),
    /**
     * The platform block this range is translated onto, on a gateway whose
     * addressTranslation is netmap. Issued by the platform — a value a caller
     * sends is ignored — and null or absent on an untranslated gateway.
     */
    mapped: cidrSchema.nullable().optional(),
  }),
);
export type ManagedSubnet = z.infer<typeof managedSubnetSchema>;

/** Normalises either shape into the current one. */
export function toManagedSubnets(value: unknown): ManagedSubnet[] {
  if (!Array.isArray(value)) return [];
  const out: ManagedSubnet[] = [];
  for (const entry of value) {
    if (typeof entry === 'string') {
      out.push({ cidr: entry });
    } else if (entry && typeof entry === 'object' && 'cidr' in entry) {
      const record = entry as { cidr: unknown; label?: unknown; mapped?: unknown };
      if (typeof record.cidr === 'string') {
        out.push({
          cidr: record.cidr,
          ...(typeof record.label === 'string' && record.label.trim()
            ? { label: record.label.trim() }
            : {}),
          ...(typeof record.mapped === 'string' && record.mapped ? { mapped: record.mapped } : {}),
        });
      }
    }
  }
  return out;
}

export const cidrsOf = (subnets: ManagedSubnet[]): string[] => subnets.map((s) => s.cidr);

/** What each range occupies ON THE TUNNEL: its mapped block when translated, itself otherwise. */
export const routedCidrsOf = (subnets: ManagedSubnet[]): string[] =>
  subnets.map((s) => s.mapped ?? s.cidr);

export const siteSchema = z.object({
  id: z.string(),
  /**
   * The short number in the gateway's URL — `/gateways/7`. Optional because a
   * row created before the backfill ran has none, and because the id remains a
   * valid address; the web builds links as `number ?? id`, like an OLT's.
   */
  number: z.number().int().nullable().optional(),
  orgId: z.string(),
  orgSlug: z.string().nullable().optional(),
  name: z.string(),
  vendor: routerVendorSchema,
  tunnelType: tunnelTypeSchema,
  /** Tunnel address handed to the remote router (peer side). */
  tunnelIp: z.string().nullable(),
  /** ACS-side tunnel address the CPEs are pointed at. */
  acsTunnelIp: z.string(),
  /** CPE management ranges reachable behind this gateway. */
  managedSubnets: z.array(managedSubnetSchema),
  /**
   * Present for a platform reader only (2026-09-18). A tenant's gateway is
   * translated by default and the tenant is never shown that it is, nor the
   * blocks: the platform's routing is not the tenant's concern. The
   * subnets' `mapped` blocks are left out for a tenant the same way.
   */
  addressTranslation: addressTranslationSchema.optional(),
  /** Platform reader only, like addressTranslation. */
  translationAt: translationAtSchema.optional(),
  /** Server translation only: the gateway's own interface on the host and the port its router dials. */
  serverListenPort: z.number().int().nullable().optional(),
  publicEndpoint: z.string().nullable(),
  status: siteStatusSchema,
  lastHandshakeAt: z.number().int().nullable(),
  rxBytes: z.number().int().nullable(),
  txBytes: z.number().int().nullable(),
  deviceCount: z.number().int().optional(),
  notes: z.string().nullable(),
  createdAt: z.number().int(),
});
export type Site = z.infer<typeof siteSchema>;

export const createSiteSchema = z.object({
  name: z.string().min(2).max(120),
  vendor: routerVendorSchema.default('mikrotik'),
  tunnelType: tunnelTypeSchema,
  managedSubnets: z.array(managedSubnetSchema).min(1).max(32),
  /** On by default: a new gateway keeps its tenant's ranges off the tunnel. */
  addressTranslation: addressTranslationSchema.default('netmap'),
  /** On the server by default: the router runs no NAT rule for it. */
  translationAt: translationAtSchema.default('server'),
  publicEndpoint: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
  /** Superadmin may place the site in another org. */
  orgId: z.string().optional(),
});
export type CreateSiteRequest = z.infer<typeof createSiteSchema>;

export const updateSiteSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  managedSubnets: z.array(managedSubnetSchema).min(1).max(32).optional(),
  /** Switching it re-issues (or drops) every range's block and re-applies the tunnel routing. */
  addressTranslation: addressTranslationSchema.optional(),
  /** Moving the translation between router and server re-homes the peer; the router must run the new script. */
  translationAt: translationAtSchema.optional(),
  /**
   * Required to switch addressTranslation on a gateway with OLTs or ONUs
   * behind it (2026-09-18). The tunnel starts routing the other addresses
   * the moment the switch is saved, and everything behind the gateway is cut
   * off until the router has run the new script — MConnect's fleet went dark
   * for an hour this way. The switch is refused by name until the caller
   * says it has read that.
   */
  acknowledgeCutover: z.boolean().optional(),
  publicEndpoint: z.string().max(200).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type UpdateSiteRequest = z.infer<typeof updateSiteSchema>;

/** What the operator pastes into (or fetches onto) the router. */
export const siteProvisioningSchema = z.object({
  siteId: z.string(),
  tunnelType: tunnelTypeSchema,
  /** RouterOS v7 script. */
  script: z.string(),
  /** `/tool fetch` one-liner that pulls the script from this ACS. */
  fetchCommand: z.string(),
  fetchUrl: z.string(),
  tokenExpiresAt: z.number().int().nullable(),
  /** WireGuard server details the operator may need to verify by hand. */
  serverPublicKey: z.string().nullable(),
  serverEndpoint: z.string().nullable(),
});
export type SiteProvisioning = z.infer<typeof siteProvisioningSchema>;
